import {
	PropsWithChildren,
	ReactElement
} from "react";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import classNames from "classnames";
import styles from "./Step.module.less";
import {
	HeightAnimationContainer,
	IconButton,
	Tag
} from "client/Components";
import { useToggle } from "client/Hooks";
import { StepTagConfiguration } from "./common";

export type StepProps = {
	title: string;
	description: string;
	trackingId: string;
	tagConfiguration: StepTagConfiguration | undefined;
	isDisabled?: boolean;
	onDelete?: () => void;
	onOpen?: () => void;
} & ({
	isCollapsible?: false;
	initialIsCollapsed?: undefined;
} | {
	isCollapsible: true;
	initialIsCollapsed?: boolean;
});

export const Step = (props: PropsWithChildren<StepProps>): ReactElement => {
	const {
		title,
		description,
		trackingId,
		isDisabled,
		isCollapsible,
		tagConfiguration,
		onDelete,
		onOpen,
		children
	} = props;

	const [isCollapsed, toggle] = useToggle(props.isCollapsible
		? props.initialIsCollapsed ?? true
		: true
	);

	const hasActions: boolean = isNotNil(onDelete)
		|| isNotNil(onOpen)
		|| isCollapsible === true;

	const renderActions = (): ReactElement | null => {
		if (!hasActions) {
			return null;
		}

		return (
			<ul className={styles.actions}>
				{onDelete && (
					<li>
						<IconButton
							kind="transparent"
							icon="trash-can"
							trackingId="delete"
							size="large"
							onClick={() => onDelete()}
							isDisabled={isDisabled}
							bubbleClick={false}
							shape="round"
						/>
					</li>
				)}

				{onOpen && (
					<li>
						<IconButton
							kind="transparent"
							icon="chevron-right"
							trackingId="open"
							size="large"
							onClick={() => onOpen()}
							isDisabled={isDisabled}
							shape="round"
						/>
					</li>
				)}

				{isCollapsible && (
					<li>
						<IconButton
							kind="transparent"
							icon={isCollapsed
								? "chevron-down"
								: "chevron-up"
							}
							trackingId="toggle-button"
							size="large"
							onClick={() => toggle()}
							isDisabled={isDisabled}
							shape="round"
						/>
					</li>
				)}
			</ul>
		);
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{ [styles.root_disabled]: isDisabled }
			)}
		>
			<div
				className={classNames(
					styles.header,
					{[styles.header_hasActions]: hasActions}
				)}
			>
				<div
					{...setTrackingId("header")}
					className={styles.headerContent}
					onClick={!isDisabled
						? isCollapsible
							? () => toggle()
							: () => onOpen?.()
						: undefined
					}
				>
					<div className={styles.title}>
						<h4
							{...setTrackingId("title")}
							className={styles.titleText}
							title={title}
						>
							{title}
						</h4>

						{isNotNil(tagConfiguration) && (
							<Tag
								{...tagConfiguration}
								trackingId="status"
							/>
						)}
					</div>

					<p
						{...setTrackingId("description")}
						className={styles.description}
						title={description}
					>
						{description}
					</p>
				</div>
				{renderActions()}
			</div>
			{isNotNil(children) && (
				isCollapsible
					? (
						<HeightAnimationContainer
							isVisible={!isCollapsed}
						>
							<div className={styles.content}>
								{children}
							</div>
						</HeightAnimationContainer>
					)
					: (
						<div className={styles.content}>
							{children}
						</div>
					)
			)}
		</div>
	);
};
Step.displayName = "Step";